import { useEffect, useRef, useState } from "react";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import useDebouncedCallback from "@builder/hooks/useDebounceCallback";
import { useTranslation } from "react-i18next";

interface TwoDSliderProps {
    min: number;
    max: number;
    x: number;
    y: number;
    callback: (x: number, y: number) => void;
    title?: string;
    reset?: () => void;
    backgroundImage?: string;
    reversed?: boolean; // Toggle inversion of axes
}

const TwoDSlider = ({ min, max, x, y, callback, title, reset, backgroundImage, reversed = false }: TwoDSliderProps) => {
    const [active, setActive] = useState<boolean>(false);
    const trackRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const debouncedCallback = useDebouncedCallback(callback, 5);
    const { t } = useTranslation();

    const handleDrag = (e: MouseEvent) => {
        if (active && trackRef.current) {
            const rect = trackRef.current.getBoundingClientRect();

            const normalizedX = (e.clientX - rect.left) / rect.width;
            const normalizedY = (e.clientY - rect.top) / rect.height;

            const newX = reversed ? max - normalizedX * (max - min) : min + normalizedX * (max - min);

            const newY = reversed ? max - normalizedY * (max - min) : min + normalizedY * (max - min);

            const clampedX = Math.min(Math.max(newX, min), max);
            const clampedY = Math.min(Math.max(newY, min), max);

            debouncedCallback(clampedX, clampedY);
        }
    };

    const handleMouseUp = () => setActive(false);

    useEffect(() => {
        if (active) {
            window.addEventListener("mousemove", handleDrag);
            window.addEventListener("mouseup", handleMouseUp);
        } else {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [active, reversed]);

    const trackStyle = {
        ...(backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {})
    };

    return (
        <div className="twoD-slider-wrapper" ref={wrapperRef}>
            <div className="twoD-title-row">
                <OliverText text={t(`generalComponents.twoDSlider.${title}`) || t("generalComponents.twoDSlider.Image Position")} size="xs" />
                {reset && (
                    <button onClick={reset}>
                        <OliverText text={t("generalComponents.twoDSlider.reset")} size="xxs" color="red" />
                    </button>
                )}
            </div>
            <div className="outer-container">
                <div className="twoD-track" ref={trackRef} style={trackStyle}>
                    <div className="twoD-inner-track">
                        <div
                            className="handle"
                            style={{
                                left: reversed ? `${100 - ((x - min) / (max - min)) * 100}%` : `${((x - min) / (max - min)) * 100}%`,
                                top: reversed ? `${100 - ((y - min) / (max - min)) * 100}%` : `${((y - min) / (max - min)) * 100}%`
                            }}
                            onMouseDown={() => setActive(true)}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoDSlider;
